import React, { useState, useEffect, useRef } from 'react';
import { LuChevronDown, LuX, LuCheck } from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';

const SearchableSelect = ({
    options = [],
    value,
    onChange,
    label,
    placeholder = 'Select an option',
    disabled = false,
    required = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const containerRef = useRef(null);

    // Initialize selected item label or search term based on value
    useEffect(() => {
        const selectedOption = options.find(opt => opt.value === value);
        if (selectedOption) {
            setSearchTerm(selectedOption.label);
        } else {
            setSearchTerm('');
        }
    }, [value, options]);

    // Filter options when search term changes
    useEffect(() => {
        // Only filter if we aren't currently showing the selected value exactly
        const selectedOption = options.find(opt => opt.value === value);
        if (isOpen) {
            const lowerSearch = searchTerm.toLowerCase();
            const filtered = options.filter(opt =>
                opt.label.toLowerCase().includes(lowerSearch)
            );
            setFilteredOptions(filtered);
        }
    }, [searchTerm, options, isOpen, value]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                // Reset search term to selected value on close if not matched
                const selectedOption = options.find(opt => opt.value === value);
                if (selectedOption) {
                    setSearchTerm(selectedOption.label);
                } else if (!value) {
                    setSearchTerm('');
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [value, options]);

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setSearchTerm('');
    };

    return (
        <div className="relative" ref={containerRef}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} {required && '*'}
                </label>
            )}
            <div
                className={`relative ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !disabled && setIsOpen(true)}
            >
                <div className="flex items-center border border-gray-300 rounded-md shadow-sm bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                    <input
                        type="text"
                        className="block w-full py-2 px-3 border-none rounded-md focus:ring-0 sm:text-sm"
                        placeholder={placeholder}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setIsOpen(true);
                        }}
                        onFocus={() => setIsOpen(true)}
                        disabled={disabled}
                    />
                    <div className="flex items-center pr-2 space-x-1">
                        {value && !disabled && (
                            <button
                                type="button"
                                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                onClick={handleClear}
                            >
                                <SafeIcon icon={LuX} className="h-4 w-4" />
                            </button>
                        )}
                        <SafeIcon icon={LuChevronDown} className="h-4 w-4 text-gray-400" />
                    </div>
                </div>
            </div>

            {isOpen && !disabled && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {filteredOptions.length === 0 ? (
                        <div className="cursor-default select-none relative py-2 px-4 text-gray-700">
                            No results found.
                        </div>
                    ) : (
                        filteredOptions.map((option) => (
                            <div
                                key={option.value}
                                className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 ${option.value === value ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-900'
                                    }`}
                                onClick={() => handleSelect(option)}
                            >
                                <span className="block truncate">{option.label}</span>
                                {option.value === value && (
                                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                                        <SafeIcon icon={LuCheck} className="h-4 w-4" />
                                    </span>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
